// Synchronized, zoomable environmental sensor charts built on uPlot.
// Three stacked charts (temperature, humidity, pressure) share one cursor and
// one x-zoom range, so hovering or zooming any chart updates all three. This
// replaces the previous independent Chart.js line charts.
//
// uPlot is loaded globally from the CDN in index.html (window.uPlot).

let activeCharts = [];
let resizeObserver = null;

const CHART_HEIGHT = 240;

// 24-hour time-axis tick formats (European style). uPlot's default uses
// 12-hour am/pm; these templates force {HH}:{mm}. Each row is
// [minTickInterval(sec), tickFmt, yearFmt, monthFmt, dayFmt, hourFmt, minFmt, secFmt, mode].
const TIME_AXIS_VALUES = [
    [3600 * 24 * 365, '{YYYY}', null, null, null, null, null, null, 1],
    [3600 * 24 * 28, '{MMM}', '\n{YYYY}', null, null, null, null, null, 1],
    [3600 * 24, '{D}/{M}', '\n{YYYY}', null, null, null, null, null, 1],
    [3600, '{HH}:{mm}', '\n{D}/{M}', null, '\n{D}/{M}', null, null, null, 1],
    [60, '{HH}:{mm}', '\n{D}/{M}', null, '\n{D}/{M}', null, null, null, 1],
    [1, '{HH}:{mm}:{ss}', '\n{D}/{M}', null, '\n{D}/{M}', null, null, null, 1],
];

/** Format the hovered x timestamp for the legend in 24-hour local time. */
function fmtLegendTime(u, ts) {
    if (ts == null) return '--';
    return window.uPlot.fmtDate('{YYYY}-{MM}-{DD} {HH}:{mm}')(new Date(ts * 1000));
}

/** Format a series value for the live legend readout. */
function fmtValue(unit, digits) {
    return (u, v) => (v == null ? '--' : v.toFixed(digits) + unit);
}

/** Draw dashed vertical separators at day boundaries (3-day / weekly views). */
function daySeparatorsPlugin(boundaries) {
    return {
        hooks: {
            draw: (u) => {
                if (!boundaries.length) return;
                const ctx = u.ctx;
                const { top, height } = u.bbox;
                const xMin = u.scales.x.min;
                const xMax = u.scales.x.max;
                ctx.save();
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
                ctx.setLineDash([5, 5]);
                ctx.lineWidth = 1;
                for (const x of boundaries) {
                    if (x < xMin || x > xMax) continue;
                    const cx = Math.round(u.valToPos(x, 'x', true)) + 0.5;
                    ctx.beginPath();
                    ctx.moveTo(cx, top);
                    ctx.lineTo(cx, top + height);
                    ctx.stroke();
                }
                ctx.restore();
            },
        },
    };
}

/** Wheel-to-zoom on the x axis, centred on the pointer. */
function wheelZoomPlugin(factor = 0.85) {
    return {
        hooks: {
            ready: (u) => {
                u.over.addEventListener('wheel', (e) => {
                    e.preventDefault();
                    const rect = u.over.getBoundingClientRect();
                    const xVal = u.posToVal(e.clientX - rect.left, 'x');
                    const xMin = u.scales.x.min;
                    const xMax = u.scales.x.max;
                    const f = e.deltaY < 0 ? factor : 1 / factor;
                    u.setScale('x', {
                        min: xVal - (xVal - xMin) * f,
                        max: xVal + (xMax - xVal) * f,
                    });
                }, { passive: false });
            },
        },
    };
}

/**
 * Render three synchronized environmental charts into `container`.
 * @param {HTMLElement} container
 * @param {{timestamps: Date[], temperatures: (number|null)[], humidities: (number|null)[], pressures: (number|null)[], period: string}} data
 * @returns {object[]} the created uPlot instances
 */
export function renderSyncedEnvCharts(container, data) {
    const uPlot = window.uPlot;
    if (!uPlot) {
        container.innerHTML =
            '<div class="text-center py-8 text-red-600 text-sm">Chart library (uPlot) failed to load.</div>';
        return [];
    }

    destroyEnvCharts();

    const { timestamps, temperatures, humidities, pressures, period } = data;
    const xs = timestamps.map(t => Math.floor(t.getTime() / 1000));

    // Day-boundary x-values for multi-day views (drawn as dashed separators).
    const boundaries = [];
    if (period === '3day' || period === 'weekly') {
        let prevDay = null;
        timestamps.forEach((t, i) => {
            const day = t.toDateString();
            if (prevDay !== null && day !== prevDay) boundaries.push(xs[i]);
            prevDay = day;
        });
    }

    // Temperature y-range: default 0-20 °C, extended to the nearest 5.
    const temps = temperatures.filter(v => v != null);
    let tMin = 0;
    let tMax = 20;
    if (temps.length) {
        const lo = Math.min(...temps);
        const hi = Math.max(...temps);
        if (lo < tMin) tMin = Math.floor(lo / 5) * 5;
        if (hi > tMax) tMax = Math.ceil(hi / 5) * 5;
    }

    container.innerHTML = `
        <div class="space-y-6">
            <div class="bg-white border rounded-lg p-4">
                <h5 class="text-sm font-medium text-gray-900 mb-3">Temperature (°C)</h5>
                <div id="env-uplot-temp" class="env-uplot"></div>
            </div>
            <div class="bg-white border rounded-lg p-4">
                <h5 class="text-sm font-medium text-gray-900 mb-3">Relative Humidity (%)</h5>
                <div id="env-uplot-humidity" class="env-uplot"></div>
            </div>
            <div class="bg-white border rounded-lg p-4">
                <h5 class="text-sm font-medium text-gray-900 mb-3">Atmospheric Pressure (hPa)</h5>
                <div id="env-uplot-pressure" class="env-uplot"></div>
            </div>
            <div class="text-xs text-gray-500 text-center">
                ${timestamps.length} data points · drag to zoom · scroll to zoom · double-click to reset
            </div>
        </div>
    `;

    const syncKey = 'env-sync';
    const showPoints = timestamps.length <= 50;
    let zoomSyncing = false;

    const propagateX = (src) => {
        if (zoomSyncing) return;
        zoomSyncing = true;
        const { min, max } = src.scales.x;
        for (const u of activeCharts) {
            if (u !== src) u.setScale('x', { min, max });
        }
        zoomSyncing = false;
    };

    const makeOpts = (mountEl, cfg) => ({
        width: mountEl.clientWidth || 600,
        height: CHART_HEIGHT,
        cursor: {
            sync: { key: syncKey, setSeries: true, scales: ['x', null] },
            drag: { x: true, y: false },
            focus: { prox: 16 },
        },
        legend: { show: true, live: true },
        scales: {
            x: { time: true },
            y: cfg.yRange ? { range: cfg.yRange } : {},
        },
        series: [
            { label: 'Time', value: fmtLegendTime },
            {
                label: cfg.label,
                stroke: cfg.stroke,
                fill: cfg.fill,
                width: 2,
                value: fmtValue(cfg.unit, cfg.digits ?? 1),
                points: { show: showPoints },
            },
        ],
        axes: [
            { values: TIME_AXIS_VALUES },
            { size: 60 },
        ],
        hooks: {
            setScale: [(u, key) => { if (key === 'x') propagateX(u); }],
        },
        plugins: [daySeparatorsPlugin(boundaries), wheelZoomPlugin()],
    });

    const tempEl = container.querySelector('#env-uplot-temp');
    const humEl = container.querySelector('#env-uplot-humidity');
    const presEl = container.querySelector('#env-uplot-pressure');

    activeCharts = [
        new uPlot(makeOpts(tempEl, {
            label: 'Temperature', stroke: 'rgb(239, 68, 68)', fill: 'rgba(239, 68, 68, 0.12)',
            unit: ' °C', yRange: [tMin, tMax],
        }), [xs, temperatures], tempEl),
        new uPlot(makeOpts(humEl, {
            label: 'Humidity', stroke: 'rgb(59, 130, 246)', fill: 'rgba(59, 130, 246, 0.12)',
            unit: ' %', yRange: [0, 100],
        }), [xs, humidities], humEl),
        new uPlot(makeOpts(presEl, {
            label: 'Pressure', stroke: 'rgb(16, 185, 129)', fill: 'rgba(16, 185, 129, 0.12)',
            unit: ' hPa',
        }), [xs, pressures], presEl),
    ];

    resizeObserver = new ResizeObserver(() => {
        for (const u of activeCharts) {
            const w = u.root.parentElement?.clientWidth;
            if (w && w !== u.width) u.setSize({ width: w, height: CHART_HEIGHT });
        }
    });
    resizeObserver.observe(container);

    return activeCharts;
}

/** Destroy any active environmental charts and stop observing resizes. */
export function destroyEnvCharts() {
    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
    for (const u of activeCharts) {
        if (u && typeof u.destroy === 'function') u.destroy();
    }
    activeCharts = [];
}
